import { IBasicSubscription, ICourse } from '@sdk/contracts';
import { useQuery } from '@tanstack/react-query';
import { dateProvider } from '@utils';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { create as createStore } from 'zustand';

import { useProfile } from '../profile';
import { useMySubscriptions } from '../subscriptions';

import { sdk } from '@/services';

type IBasicCourse = Pick<ICourse, '_id' | 'name' | 'emoji' | 'tags' | 'isPlanificable'>;
interface IStore {
  course?: IBasicCourse;
  myPosition: number;
  subscriptionsList: IBasicSubscription[];
  selectedMonth: number;
  selectedYear: number;
  setCourse: (course: IBasicCourse) => void;
  setMyPosition: (position: number) => void;
  setSubscriptionsList: (subscriptions: IBasicSubscription[]) => void;
  setSelectedMonth: (month: number) => void;
  setSelectedYear: (year: number) => void;
}

const store = createStore<IStore>(set => ({
  subscriptionsList: [],
  myPosition: -1,
  selectedMonth: dateProvider.now().getMonth(),
  selectedYear: dateProvider.now().getFullYear(),
  setCourse: (course: IBasicCourse) => set({ course }),
  setMyPosition: (position: number) => set({ myPosition: position }),
  setSubscriptionsList: (subscriptions: IBasicSubscription[]) =>
    set({ subscriptionsList: subscriptions }),
  setSelectedMonth: (month: number) => set({ selectedMonth: month }),
  setSelectedYear: (year: number) => set({ selectedYear: year })
}));

const SubscriptionService = sdk.getService('SubscriptionService');

export const useRankingByCourse = () => {
  const { _id } = useParams<{ _id: string }>();
  const {
    course,
    myPosition,
    subscriptionsList,
    selectedMonth,
    selectedYear,
    setCourse,
    setMyPosition,
    setSubscriptionsList,
    setSelectedMonth,
    setSelectedYear
  } = store();
  const { profile } = useProfile();
  const userId = profile?._id;
  const { filteredSubscriptions, selectedSubscription } = useMySubscriptions();

  const courseId = _id ?? course?._id;

  const { isLoading, data, error } = useQuery({
    queryKey: ['ranking', courseId, selectedMonth, selectedYear],
    queryFn: () => SubscriptionService.getRankingByPeriod(courseId!, selectedMonth, selectedYear),
    enabled: !!courseId,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (!course && filteredSubscriptions?.length && selectedSubscription) {
      setCourse(selectedSubscription.course);
      setSubscriptionsList(filteredSubscriptions);
    }
  }, [filteredSubscriptions, selectedSubscription]);

  useEffect(() => {
    if (data) {
      const position = data.findIndex(r => r._id === userId);
      setMyPosition(position);
    }
  }, [data, userId]);

  return {
    ranking: data,
    course,
    myPosition,
    subscriptionsList,
    selectedMonth,
    selectedYear,
    setCourse,
    setSelectedMonth,
    setSelectedYear,
    isLoading,
    error
  };
};
